/**
 * Flagged Item modal body: orange banner + unified table / accordion / no-remaining / editable line.
 */
import React, { useContext } from 'react';
import {
    Box,
    Editable,
    EditableInput,
    EditablePreview,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Table,
    Tag,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Tooltip,
    Button,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import SortArrowIcon from '../../components/SortArrowIcon';
import type { SortIconDirection } from '../../components/SortArrowIcon';
import SessionContext from '../../contexts/SessionContext';
import { isInvoicingDemoSession } from '../../demo/invoicingDemo';
import { getLineStatusTagColor, getLineStatusDisplayText } from '../../utils/lineStatusTag';
import { getQtyColumnLabel, getQtyColumnTooltip, getQtyNexDescriptor, type NexLineQuantityBasis } from '../../utils/nexLineQuantity';
import type { InvoiceDetailResponse, InvoicePart } from '../../types/invoice';
import {
    firstVisibleNexAccordionKey,
    firstVisibleNexOptionKey,
    nexQtyReceivedForDisplay,
    NO_VISIBLE_NEX_ACCORDION_KEY,
    type GetFlaggedItemMatchOptionsResult,
    type InvoicePartWithNex,
} from './nexOptionUtils';
import { fmtUsd } from './LineCard';
import { parseSignedQtyCommit } from '../../utils/invoiceNumericInput';
import {
    defaultNexSuggestionFilterLabel,
    nexSuggestionPassesDefaultCiecaFilter,
} from '../../utils/invoiceNexSuggestionCiecaFilter';

const ACCENT = '#0071E3';

type SuggestedSortKey =
    | 'description'
    | 'partNumber'
    | 'poLine'
    | 'orderedDate'
    | 'lineStatus'
    | 'qty'
    | 'listPrice'
    | 'unitCost'
    | 'total';

type SuggestedSortState = {
    key: SuggestedSortKey;
    direction: 'asc' | 'desc';
} | null;

function formatDateCell(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US');
}

function compareNumbers(a: number, b: number): number {
    return a === b ? 0 : a < b ? -1 : 1;
}

function compareStrings(a: string, b: string): number {
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function compareDates(a: string | Date | null | undefined, b: string | Date | null | undefined): number {
    const ams = a ? new Date(a).getTime() : Number.NEGATIVE_INFINITY;
    const bms = b ? new Date(b).getTime() : Number.NEGATIVE_INFINITY;
    if (Number.isNaN(ams) && Number.isNaN(bms)) return 0;
    if (Number.isNaN(ams)) return -1;
    if (Number.isNaN(bms)) return 1;
    return compareNumbers(ams, bms);
}


function LineStatusTag({ status }: { status: string | undefined }) {
    if (!status || status === '—' || status.trim() === '') return <Text fontSize="sm" color="gray.400">—</Text>;
    return <Tag size="sm" colorScheme={getLineStatusTagColor(status)} variant="subtle" fontWeight="medium">{getLineStatusDisplayText(status)}</Tag>;
}

function getPoLineSortValue(opt: InvoicePartWithNex): number {
    const po = Number(opt.poHeaderID ?? 0);
    const ln = Number(opt.lineNumber ?? 0);
    return po * 100000 + ln;
}

function getOptionTotal(opt: InvoicePartWithNex): number {
    return Number(opt.totalBuyPrice ?? 0);
}

/** Permutation of indices into `options` after applying column sort (stable for Match / accordion keys). */
function sortSuggestedOptionIndices(
    options: InvoicePartWithNex[],
    sortState: SuggestedSortState,
    nexLineQuantityBasis: NexLineQuantityBasis = 'qtyRec',
): number[] {
    const idxs = options.map((_, i) => i);
    if (!sortState) return idxs;
    const dir = sortState.direction === 'asc' ? 1 : -1;
    return [...idxs].sort((i, j) => {
        const a = options[i];
        const b = options[j];
        let cmp = 0;
        switch (sortState.key) {
            case 'description':
                cmp = compareStrings(a.title ?? '', b.title ?? '');
                break;
            case 'partNumber':
                cmp = compareStrings(a.partNumber ?? '', b.partNumber ?? '');
                break;
            case 'poLine':
                cmp = compareNumbers(getPoLineSortValue(a), getPoLineSortValue(b));
                break;
            case 'orderedDate':
                cmp = compareDates(a._poOrderedDate, b._poOrderedDate);
                break;
            case 'lineStatus':
                cmp = compareStrings(a.lineStatus ?? '', b.lineStatus ?? '');
                break;
            case 'qty':
                cmp = compareNumbers(
                    nexQtyReceivedForDisplay(a, nexLineQuantityBasis),
                    nexQtyReceivedForDisplay(b, nexLineQuantityBasis),
                );
                break;
            case 'listPrice':
                cmp = compareNumbers(Number(a.listPrice ?? 0), Number(b.listPrice ?? 0));
                break;
            case 'unitCost':
                cmp = compareNumbers(Number(a.unitBuyPrice ?? 0), Number(b.unitBuyPrice ?? 0));
                break;
            case 'total':
                cmp = compareNumbers(getOptionTotal(a), getOptionTotal(b));
                break;
        }
        return cmp * dir;
    });
}

function nexSuggestionSearchHaystack(
    opt: InvoicePartWithNex,
    nexLineQuantityBasis: NexLineQuantityBasis = 'qtyRec',
): string {
    const ext = opt as unknown as Record<string, unknown>;
    const poHeaderID = ext.poHeaderID ?? ext.po_header_id;
    const lineNumber = ext.lineNumber ?? ext.line_number;
    const poLine =
        poHeaderID != null && poHeaderID !== ''
            ? `${String(poHeaderID)} ${String(lineNumber ?? '')}`
            : '';
    const ord = typeof ext._poOrderedDate === 'string' ? ext._poOrderedDate : opt._poOrderedDate;
    const lineStatusRaw = typeof ext.lineStatus === 'string' ? ext.lineStatus : opt.lineStatus;
    const lineStatus = lineStatusRaw != null && String(lineStatusRaw).trim() !== '' ? String(lineStatusRaw) : '';
    const pieces: Array<string | number | undefined | null> = [
        opt.title,
        opt.partNumber,
        poLine,
        formatDateCell(ord),
        lineStatus,
        opt.qty,
        nexQtyReceivedForDisplay(opt, nexLineQuantityBasis),
        opt.listPrice != null ? opt.listPrice : null,
        opt.listPrice != null ? fmtUsd(opt.listPrice) : null,
        opt.unitBuyPrice != null ? opt.unitBuyPrice : null,
        opt.unitBuyPrice != null ? fmtUsd(opt.unitBuyPrice) : null,
        opt.totalBuyPrice != null ? opt.totalBuyPrice : null,
        opt.totalBuyPrice != null ? fmtUsd(opt.totalBuyPrice) : null,
    ];
    return pieces
        .filter((p) => p != null && String(p).length > 0)
        .map((p) => String(p))
        .join(' ')
        .toLowerCase();
}

function nexSuggestionMatchesQuery(
    opt: InvoicePartWithNex,
    queryRaw: string,
    nexLineQuantityBasis: NexLineQuantityBasis = 'qtyRec',
): boolean {
    const q = queryRaw.trim().toLowerCase();
    if (!q) return true;
    return nexSuggestionSearchHaystack(opt, nexLineQuantityBasis).includes(q);
}

function searchedSuggestedLineIndices(
    lines: InvoicePartWithNex[],
    sortState: SuggestedSortState,
    suggestionSearch: string,
    nexLineQuantityBasis?: NexLineQuantityBasis,
): number[] {
    const ordered = sortSuggestedOptionIndices(lines, sortState, nexLineQuantityBasis);
    if (!suggestionSearch.trim()) return ordered;
    return ordered.filter((li) =>
        nexSuggestionMatchesQuery(lines[li], suggestionSearch, nexLineQuantityBasis),
    );
}

function visibleSuggestedLineIndices(
    lines: InvoicePartWithNex[],
    args: {
        sortState: SuggestedSortState;
        suggestionSearch: string;
        showAllCieca: boolean;
        invoiceType?: InvoiceDetailResponse['invoiceType'];
        invoicePartNumber?: string | null;
        invoiceVendorName?: string | null;
        nexLineQuantityBasis?: NexLineQuantityBasis;
    },
): number[] {
    const searched = searchedSuggestedLineIndices(
        lines,
        args.sortState,
        args.suggestionSearch,
        args.nexLineQuantityBasis,
    );
    if (args.showAllCieca) return searched;
    return searched.filter((li) =>
        nexSuggestionPassesDefaultCiecaFilter({
            invoiceType: args.invoiceType,
            invoicePartNumber: args.invoicePartNumber,
            nexLine: lines[li],
            invoiceVendorName: args.invoiceVendorName,
        }),
    );
}

function QtyColumnHeader({
    sortState,
    toggleSort,
    qtyColumnLabel,
    qtyColumnTooltip,
    compact,
}: {
    sortState: SuggestedSortState;
    toggleSort: (key: SuggestedSortKey) => void;
    qtyColumnLabel: string;
    qtyColumnTooltip: string;
    /** Slightly smaller label for accordion tables. */
    compact?: boolean;
}) {
    return (
        <Th py={compact ? 2 : 3} w="6%" textAlign="center" whiteSpace="nowrap">
            <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                <Box display="inline-block" w="100%" whiteSpace="nowrap">
                    {renderSortableHeader(qtyColumnLabel, 'qty', sortState, toggleSort, true)}
                </Box>
            </Tooltip>
        </Th>
    );
}

function renderSortableHeader(
    label: string,
    key: SuggestedSortKey,
    sortState: SuggestedSortState,
    toggleSort: (key: SuggestedSortKey) => void,
    isNumeric: boolean = false,
) {
    const isActive = sortState?.key === key;
    const direction: SortIconDirection = isActive ? sortState!.direction : 'none';

    return (
        <Flex
            as="button"
            type="button"
            align="center"
            justify={isNumeric ? 'flex-end' : 'flex-start'}
            gap="6px"
            flexWrap="nowrap"
            w="100%"
            color={isActive ? '#005BC4' : 'gray.600'}
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="0.05em"
            cursor="pointer"
            onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                toggleSort(key);
            }}
            _hover={{ color: '#005BC4' }}
            transition="color 0.2s"
        >
            <Text as="span">{label}</Text>
            <Box lineHeight={0} color={isActive ? '#005BC4' : 'gray.400'}>
                <SortArrowIcon direction={direction} size={12} />
            </Box>
        </Flex>
    );
}

export interface FlaggedItemContentProps {
    idx: number;
    item: InvoicePart;
    issues: any[];
    choiceIssue: any;
    isUserUnmatchedLine: boolean;
    getIssueTooltipText: (issue: any, hasLocalMatch?: boolean, asString?: boolean) => any;
    matchResult: GetFlaggedItemMatchOptionsResult | null;
    selectedNexOptionKey: string | null;
    setSelectedNexOptionKey: React.Dispatch<React.SetStateAction<string | null>>;
    selectedNexOptionAccordionKey: string | null;
    setSelectedNexOptionAccordionKey: React.Dispatch<React.SetStateAction<string | null>>;
    invoice: InvoiceDetailResponse | undefined;
    editedFields: Partial<InvoiceDetailResponse>;
    updateLineItem: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], value: any) => void;
    getLineItemStringValue: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], defaultValue?: string) => string;
    renderLineItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    /** When set, invoice row edits use ignored-tab callbacks and row index instead of `idx` (global part index). */
    tabularEdit?: { kind: 'ignored'; rowIndex: number };
    updateIgnoredPart?: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], value: any) => void;
    getIgnoredPartStringValue?: (index: number, field: keyof InvoiceDetailResponse['lineItems'][0], defaultValue?: string) => string;
    renderIgnoredPartNumericCell?: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    /** Org setting: which NEX qty field to show for received/sublet suggestions. */
    nexLineQuantityBasis?: NexLineQuantityBasis;
}

export function FlaggedItemContent(props: FlaggedItemContentProps) {
    const {
        idx,
        item,
        issues,
        choiceIssue,
        isUserUnmatchedLine,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- banner moved to modal header
        getIssueTooltipText,
        matchResult,
        selectedNexOptionKey,
        setSelectedNexOptionKey,
        selectedNexOptionAccordionKey,
        setSelectedNexOptionAccordionKey,
        invoice,
        editedFields,
        updateLineItem,
        getLineItemStringValue,
        renderLineItemNumericCell,
        tabularEdit,
        updateIgnoredPart,
        getIgnoredPartStringValue,
        renderIgnoredPartNumericCell,
        nexLineQuantityBasis = 'qtyRec',
    } = props;

    const editIgnored = tabularEdit?.kind === 'ignored' && updateIgnoredPart && getIgnoredPartStringValue && renderIgnoredPartNumericCell;
    const editRowIndex = editIgnored ? tabularEdit.rowIndex : idx;
    const getStr = (field: keyof InvoiceDetailResponse['lineItems'][0], def: string) =>
        editIgnored ? getIgnoredPartStringValue!(editRowIndex, field, def) : getLineItemStringValue(editRowIndex, field, def);
    const setField = (field: keyof InvoiceDetailResponse['lineItems'][0], value: any) =>
        editIgnored ? updateIgnoredPart!(editRowIndex, field, value) : updateLineItem(editRowIndex, field, value);
    const renderNum = (field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) =>
        editIgnored ? renderIgnoredPartNumericCell!(editRowIndex, field, fw) : renderLineItemNumericCell(editRowIndex, field, fw);
    const qtyColumnLabel = getQtyColumnLabel(invoice?.invoiceType, nexLineQuantityBasis);
    const qtyColumnTooltip = getQtyColumnTooltip(invoice?.invoiceType, nexLineQuantityBasis);

    const issueWithOptionsForEditable = choiceIssue ?? issues.find((i: any) =>
        (i.issueType === 'missing_in_nex' || i.issueType === 'different_part_names' || i.issueType === 'different_positions') &&
        (i.issueDetails?.suggestedPartNumber || (i.issueDetails?.suggestedNexLinesFromVendor?.length) || i.issueDetails?.suggestedLine || i.issueDetails?.nexOptionsByVendor?.length)
    );
    const anyIssueWithNexOptions = invoice?.reconciliationIssues?.flaggedParts?.find(
        (f: any) => f.issueDetails?.nexOptionsByVendor?.some((v: any) => v.lines?.length > 0)
    );
    // Negative indices are used for Freight; ignored return-slip rows use `tabularEdit` + ignored callbacks.
    const showEditableTable = (editIgnored || idx >= 0) && !isUserUnmatchedLine &&
        !issueWithOptionsForEditable &&
        !((matchResult?.nexOptionsByVendor?.length ?? 0) > 0) &&
        !anyIssueWithNexOptions;

    return (
        <VStack spacing={4} align="stretch" flex="1" minH={0} overflow="hidden">
            {matchResult &&
                (matchResult.missingInNexIssue ||
                    matchResult.issueWithOptions ||
                    matchResult.issueOfRelevantType ||
                    isUserUnmatchedLine ||
                    matchResult.options.length > 0 ||
                    (matchResult.nexOptionsByVendor?.some((v) => (v.lines?.length ?? 0) > 0) ?? false)) && (
                <>
                    {(
                        matchResult.showUnifiedModal ? (
                            <UnifiedOptionsTable
                                item={item}
                                options={matchResult.options}
                                vendorName={matchResult.vendorName}
                                invoiceDate={editedFields.timestamp ?? invoice?.timestamp}
                                invoiceType={invoice?.invoiceType}
                                invoiceVendorName={editedFields.supplierText ?? invoice?.supplierText}
                                nexLineQuantityBasis={nexLineQuantityBasis}
                                selectedNexOptionKey={selectedNexOptionKey}
                                setSelectedNexOptionKey={setSelectedNexOptionKey}
                            />
                        ) : matchResult.nexOptionsByVendor?.some((v) => (v.lines?.length ?? 0) > 0) ? (
                            <AccordionOptions
                                item={item}
                                nexOptionsByVendor={matchResult.nexOptionsByVendor!}
                                invoiceDate={editedFields.timestamp ?? invoice?.timestamp}
                                invoiceType={invoice?.invoiceType}
                                invoiceVendorName={editedFields.supplierText ?? invoice?.supplierText}
                                nexLineQuantityBasis={nexLineQuantityBasis}
                                selectedNexOptionAccordionKey={selectedNexOptionAccordionKey}
                                setSelectedNexOptionAccordionKey={setSelectedNexOptionAccordionKey}
                            />
                        ) : matchResult.fromIssueWithOptions && matchResult.baseOptionsFromSuggestion.length === 0 ? (
                            <NoRemainingBlock item={item} />
                        ) : null
                    )}
                </>
            )}


            {showEditableTable && (
                <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
                        <Thead bg="gray.50">
                            <Tr>
                                <Th py={3} w="25%" color="gray.500" fontSize="xs">Description</Th>
                                <Th py={3} w="22%" minW="120px" color="gray.500" fontSize="xs">Part #</Th>
                                <Th py={3} w="8%" textAlign="center" color="gray.500" fontSize="xs" whiteSpace="nowrap">
                                    <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                        <Text
                                            as="span"
                                            display="inline-block"
                                            cursor="help"
                                            textTransform="uppercase"
                                            letterSpacing="0.04em"
                                            whiteSpace="nowrap"
                                        >
                                            {qtyColumnLabel}
                                        </Text>
                                    </Tooltip>
                                </Th>
                                <Th py={3} w="16%" minW="90px" textAlign="right" color="gray.500" fontSize="xs">List Price</Th>
                                <Th py={3} w="16%" minW="90px" textAlign="right" color="gray.500" fontSize="xs">Buy Price</Th>
                                <Th py={3} w="14%" minW="90px" textAlign="right" color="gray.500" fontSize="xs">Total</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td py={3} fontSize="sm" borderColor="gray.100">
                                    <Editable value={getStr('title', '')} onChange={(v) => setField('title', v)} placeholder="Description">
                                        <EditablePreview w="100%" cursor="pointer" color="gray.900" py={0} px={0} />
                                        <EditableInput border="none" outline="1.5px solid" outlineColor="blue.300" borderRadius="md" color="gray.900" bg="blue.50" py="2px" px={1} h="auto" lineHeight="short" _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0); }} />
                                    </Editable>
                                </Td>
                                <Td py={3} fontSize="sm" borderColor="gray.100" whiteSpace="nowrap" minW="120px">
                                    <Editable value={getStr('partNumber', '')} onChange={(v) => setField('partNumber', v)} placeholder="Part #">
                                        <EditablePreview w="100%" cursor="pointer" color="gray.900" py={0} px={0} whiteSpace="nowrap" />
                                        <EditableInput border="none" outline="1.5px solid" outlineColor="blue.300" borderRadius="md" color="gray.900" bg="blue.50" py="2px" px={1} h="auto" lineHeight="short" _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0); }} />
                                    </Editable>
                                </Td>
                                <Td py={3} fontSize="sm" textAlign="center" borderColor="gray.100">
                                    <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                        <Box as="span" display="block">
                                            <Editable value={getStr('qty', '0')} onChange={(v) => setField('qty', parseSignedQtyCommit(v))}>
                                                <EditablePreview w="100%" cursor="pointer" textAlign="center" color="gray.900" py={0} px={0} />
                                                <EditableInput type="text" inputMode="text" textAlign="center" border="none" outline="1.5px solid" outlineColor="blue.300" borderRadius="md" color="gray.900" bg="blue.50" py="2px" px={1} h="auto" lineHeight="short" _focus={{ outline: '1.5px solid', outlineColor: ACCENT, boxShadow: 'none' }} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0); }} />
                                            </Editable>
                                        </Box>
                                    </Tooltip>
                                </Td>
                                <Td py={3} fontSize="sm" textAlign="right" borderColor="gray.100" whiteSpace="nowrap" minW="90px">{renderNum('listPrice')}</Td>
                                <Td py={3} fontSize="sm" textAlign="right" borderColor="gray.100" whiteSpace="nowrap" minW="90px">{renderNum('unitBuyPrice')}</Td>
                                <Td py={3} fontSize="sm" textAlign="right" fontWeight="medium" borderColor="gray.100" whiteSpace="nowrap" minW="90px">{renderNum('totalBuyPrice', 'medium')}</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>
            )}
        </VStack>
    );
}

function UnifiedOptionsTable({
    item,
    options,
    vendorName,
    invoiceDate,
    invoiceType,
    invoiceVendorName,
    nexLineQuantityBasis = 'qtyRec',
    selectedNexOptionKey,
    setSelectedNexOptionKey,
}: {
    item: InvoicePart;
    options: InvoicePartWithNex[];
    vendorName: string;
    invoiceDate?: string | Date;
    invoiceType?: InvoiceDetailResponse['invoiceType'];
    invoiceVendorName?: string | null;
    nexLineQuantityBasis?: NexLineQuantityBasis;
    selectedNexOptionKey: string | null;
    setSelectedNexOptionKey: React.Dispatch<React.SetStateAction<string | null>>;
}) {
    const sessionContext = useContext(SessionContext);
    const showLineStatus = !isInvoicingDemoSession(sessionContext);
    const descriptionColW = showLineStatus ? '19%' : '29%';
    const [sortState, setSortState] = React.useState<SuggestedSortState>(null);
    const [suggestionSearch, setSuggestionSearch] = React.useState('');
    const orderedIndices = React.useMemo(
        () => sortSuggestedOptionIndices(options, sortState, nexLineQuantityBasis),
        [options, sortState, nexLineQuantityBasis]
    );
    const filteredIndices = React.useMemo(() => {
        if (!suggestionSearch.trim()) return orderedIndices;
        return orderedIndices.filter((i) =>
            nexSuggestionMatchesQuery(options[i], suggestionSearch, nexLineQuantityBasis),
        );
    }, [orderedIndices, options, suggestionSearch, nexLineQuantityBasis]);
    const [showAllCieca, setShowAllCieca] = React.useState(false);
    const ciecaFilteredIndices = React.useMemo(() => {
        if (showAllCieca) return filteredIndices;
        return filteredIndices.filter((i) =>
            nexSuggestionPassesDefaultCiecaFilter({
                invoiceType,
                invoicePartNumber: item?.partNumber,
                nexLine: options[i],
                invoiceVendorName,
            }),
        );
    }, [filteredIndices, showAllCieca, invoiceType, invoiceVendorName, item?.partNumber, options]);
    const hiddenCiecaCount = filteredIndices.length - ciecaFilteredIndices.length;
    const ciecaFilterLabel = defaultNexSuggestionFilterLabel(invoiceType);
    React.useLayoutEffect(() => {
        const next = firstVisibleNexOptionKey(ciecaFilteredIndices, selectedNexOptionKey);
        const current = selectedNexOptionKey ?? '0';
        if (next !== current) setSelectedNexOptionKey(next);
    }, [ciecaFilteredIndices, selectedNexOptionKey, setSelectedNexOptionKey]);
    const toggleSort = (key: SuggestedSortKey) => {
        setSortState((prev) => {
            if (!prev || prev.key !== key) return { key, direction: 'asc' };
            if (prev.direction === 'asc') return { key, direction: 'desc' };
            return null;
        });
    };
    const qtyColumnLabel = getQtyColumnLabel(invoiceType, nexLineQuantityBasis);
    const qtyColumnTooltip = getQtyColumnTooltip(invoiceType, nexLineQuantityBasis);
    const qtyNexDescriptor = getQtyNexDescriptor(invoiceType, nexLineQuantityBasis);
    return (
        <Flex direction="column" flex="1" minH={0} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
            <Box flexShrink={0}>
                <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed' }}>
                    <Thead>
                        <Tr bg="gray.50">
                            <Th py={3} w="11%">{renderSortableHeader('PO / Line', 'poLine', sortState, toggleSort)}</Th>
                            <Th py={3} w="11%">{renderSortableHeader('Ordered Date', 'orderedDate', sortState, toggleSort)}</Th>
                            {showLineStatus && (
                                <Th py={3} w="10%">{renderSortableHeader('Line Status', 'lineStatus', sortState, toggleSort)}</Th>
                            )}
                            <Th py={3} w={descriptionColW}>{renderSortableHeader('Description', 'description', sortState, toggleSort)}</Th>
                            <Th py={3} w="12%">{renderSortableHeader('Part Number', 'partNumber', sortState, toggleSort)}</Th>
                            <QtyColumnHeader sortState={sortState} toggleSort={toggleSort} qtyColumnLabel={qtyColumnLabel} qtyColumnTooltip={qtyColumnTooltip} />
                            <Th py={3} w="11%">{renderSortableHeader('List Price', 'listPrice', sortState, toggleSort, true)}</Th>
                            <Th py={3} w="11%">{renderSortableHeader('Unit Cost', 'unitCost', sortState, toggleSort, true)}</Th>
                            <Th py={3} w="9%">{renderSortableHeader('Total', 'total', sortState, toggleSort, true)}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        <Tr bg="white">
                            <Td py={3} fontSize="sm" color="gray.500" borderColor="gray.100">—</Td>
                            <Td py={3} fontSize="sm" color="gray.500" borderColor="gray.100">{formatDateCell(invoiceDate)}</Td>
                            {showLineStatus && (
                                <Td py={3} fontSize="sm" borderColor="gray.100"><LineStatusTag status={undefined} /></Td>
                            )}
                            <Td py={3} fontSize="sm" borderColor="gray.100">{item?.title ?? '—'}</Td>
                            <Td py={3} fontSize="sm" borderColor="gray.100">{item?.partNumber ?? '—'}</Td>
                            <Td py={3} fontSize="sm" textAlign="center" borderColor="gray.100">
                                <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                    <Text as="span" display="inline-block">{item?.qty ?? '—'}</Text>
                                </Tooltip>
                            </Td>
                            <Td py={3} fontSize="sm" textAlign="right" borderColor="gray.100">{item?.listPrice != null ? fmtUsd(item.listPrice) : '—'}</Td>
                            <Td py={3} fontSize="sm" textAlign="right" borderColor="gray.100">{item?.unitBuyPrice != null ? fmtUsd(item.unitBuyPrice) : '—'}</Td>
                            <Td py={3} fontSize="sm" textAlign="right" fontWeight="medium" borderColor="gray.100">{item?.totalBuyPrice != null ? fmtUsd(item.totalBuyPrice) : '—'}</Td>
                        </Tr>
                    </Tbody>
                </Table>
            </Box>
            <Box borderTopWidth="1px" borderColor="gray.200" bg="white" flex="1" minH={0} overflowY="auto">
                <Box
                    position="sticky"
                    top={0}
                    zIndex={2}
                    w="100%"
                    bg="white"
                    borderBottomWidth="1px"
                    borderBottomColor="gray.100"
                    px={3}
                    py={2}
                >
                    <Flex
                        direction={{ base: 'column', lg: 'row' }}
                        align={{ base: 'stretch', lg: 'center' }}
                        gap={3}
                        justify="space-between"
                        w="100%"
                    >
                        <Text fontSize="sm" color="gray.700" flex="1" minW={0}>
                            These are the remaining line items from <strong>{vendorName}</strong> in the NEX response (already-matched lines are excluded).
                            {ciecaFilterLabel && hiddenCiecaCount > 0 && !showAllCieca ? (
                                <> {ciecaFilterLabel}. </>
                            ) : null}
                        </Text>
                        <Flex align="center" gap={2} flexShrink={0}>
                            {hiddenCiecaCount > 0 || showAllCieca ? (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="blue"
                                    onClick={() => setShowAllCieca((v) => !v)}
                                >
                                    {showAllCieca ? "Show fewer" : `Show more (${hiddenCiecaCount})`}
                                </Button>
                            ) : null}
                        <InputGroup
                            size="sm"
                            flexShrink={0}
                            flex={{ base: 'none', lg: '0 0 25%' }}
                            w="25%"
                            maxW="25%"
                            alignSelf={{ base: 'flex-end', lg: 'stretch' }}
                        >
                            <InputLeftElement pointerEvents="none" h="32px">
                                <SearchIcon color="gray.400" boxSize={3.5} />
                            </InputLeftElement>
                            <Input
                                w="100%"
                                minW={0}
                                pl={9}
                                placeholder="Search by description, part #, PO, status, amounts…"
                                value={suggestionSearch}
                                onChange={(e) => setSuggestionSearch(e.target.value)}
                                bg="white"
                                borderColor="gray.200"
                            />
                        </InputGroup>
                        </Flex>
                    </Flex>
                </Box>
                {filteredIndices.length === 0 ? (
                    <Box px={3} py={8} textAlign="center">
                        <Text fontSize="sm" color="gray.500">No NEX lines match this search.</Text>
                    </Box>
                ) : ciecaFilteredIndices.length === 0 ? (
                    <Box px={3} py={8} textAlign="center">
                        <Text fontSize="sm" color="gray.500">
                            No {invoiceType === "sublet" ? "sublet (PAS/PASL)" : "OEM/aftermarket"} lines match. Use Show more to see every remaining NEX line.
                        </Text>
                    </Box>
                ) : (
                <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed' }}>
                    <Thead bg="gray.50">
                        <Tr>
                            <Th py={3} w="11%">{renderSortableHeader('PO / Line', 'poLine', sortState, toggleSort)}</Th>
                            <Th py={3} w="11%">{renderSortableHeader('Ordered Date', 'orderedDate', sortState, toggleSort)}</Th>
                            {showLineStatus && (
                                <Th py={3} w="10%">{renderSortableHeader('Line Status', 'lineStatus', sortState, toggleSort)}</Th>
                            )}
                            <Th py={3} w={descriptionColW}>{renderSortableHeader('Description', 'description', sortState, toggleSort)}</Th>
                            <Th py={3} w="12%">{renderSortableHeader('Part Number', 'partNumber', sortState, toggleSort)}</Th>
                            <QtyColumnHeader sortState={sortState} toggleSort={toggleSort} qtyColumnLabel={qtyColumnLabel} qtyColumnTooltip={qtyColumnTooltip} />
                            <Th py={3} w="11%">{renderSortableHeader('List Price', 'listPrice', sortState, toggleSort, true)}</Th>
                            <Th py={3} w="11%">{renderSortableHeader('Unit Cost', 'unitCost', sortState, toggleSort, true)}</Th>
                            <Th py={3} w="9%">{renderSortableHeader('Total', 'total', sortState, toggleSort, true)}</Th>
                        </Tr>
                    </Thead>
                    {ciecaFilteredIndices.map((originalIdx: number) => {
                        const opt = options[originalIdx];
                        const ext = opt as unknown as Record<string, unknown>;
                        const poHeaderID = ext.poHeaderID ?? ext.po_header_id;
                        const lineNumber = ext.lineNumber ?? ext.line_number;
                        const fullQty = ext.fullQty ?? ext.full_qty;
                        const consumed = ext.consumed;
                        const orderedDate = typeof ext._poOrderedDate === 'string' ? ext._poOrderedDate : undefined;
                        const rowKey = String(originalIdx);
                        const isSelected = rowKey === (selectedNexOptionKey ?? '0');
                        const consumedNum = typeof consumed === 'number' ? consumed : 0;
                        const fullQtyNum = typeof fullQty === 'number' ? fullQty : null;
                        const hasConsumedNote = consumedNum > 0 && fullQtyNum != null;
                        const nexRecQty = nexQtyReceivedForDisplay(opt, nexLineQuantityBasis);
                        const qtyValue = nexRecQty;
                        const lineStatus = typeof ext.lineStatus === 'string' && ext.lineStatus.trim() !== '' ? ext.lineStatus : '—';
                        return (
                            <Tbody
                                key={originalIdx}
                                bg={isSelected ? 'blue.50' : 'white'}
                                borderLeft={isSelected ? '4px solid' : 'none'}
                                borderLeftColor={isSelected ? 'blue.500' : 'transparent'}
                                _hover={{ bg: isSelected ? 'blue.100' : 'gray.100', cursor: 'pointer' }}
                                onClick={() => setSelectedNexOptionKey(rowKey)}
                                borderBottom="1px solid #E2E8F0"
                            >
                                <Tr>
                                    <Td py={3} w="11%" fontSize="xs" fontFamily="mono" borderBottom="none" whiteSpace="nowrap">
                                        {poHeaderID != null && poHeaderID !== ''
                                            ? `${String(poHeaderID)} / L${lineNumber != null && lineNumber !== '' ? String(lineNumber) : '—'}`
                                            : '—'}
                                    </Td>
                                    <Td py={3} w="11%" fontSize="sm" borderBottom="none">{formatDateCell(orderedDate)}</Td>
                                    {showLineStatus && (
                                        <Td py={3} w="10%" borderBottom="none"><LineStatusTag status={lineStatus === '—' ? undefined : lineStatus} /></Td>
                                    )}
                                    <Td py={3} w={descriptionColW} fontSize="sm" borderBottom="none">{opt.title ?? '—'}</Td>
                                    <Td py={3} w="12%" fontSize="sm" borderBottom="none">{opt.partNumber ?? '—'}</Td>
                                    <Td py={3} w="6%" fontSize="sm" textAlign="center" borderBottom="none">
                                        {hasConsumedNote ? (
                                            <Tooltip
                                                label={`${fullQtyNum} NEX ${qtyNexDescriptor} before ${consumedNum} unit(s) matched on other invoice lines. ${qtyColumnTooltip}`}
                                                placement="top"
                                                hasArrow
                                            >
                                                <Text as="span" cursor="help">{qtyValue}*</Text>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                                <Text as="span" display="inline-block">{qtyValue}</Text>
                                            </Tooltip>
                                        )}
                                    </Td>
                                    <Td py={3} w="11%" fontSize="sm" textAlign="right" borderBottom="none">{opt.listPrice != null ? fmtUsd(opt.listPrice) : '—'}</Td>
                                    <Td py={3} w="11%" fontSize="sm" textAlign="right" borderBottom="none">{opt.unitBuyPrice != null ? fmtUsd(opt.unitBuyPrice) : '—'}</Td>
                                    <Td py={3} w="9%" fontSize="sm" textAlign="right" fontWeight="medium" borderBottom="none">{opt.totalBuyPrice != null ? fmtUsd(opt.totalBuyPrice) : '—'}</Td>
                                </Tr>
                            </Tbody>
                        );
                    })}
                </Table>
                )}
            </Box>
        </Flex>
    );
}

function NoRemainingBlock({ item: _item }: { item: InvoicePart }) {
    return (
        <Flex align="center" justify="center" py={10}>
            <Text fontSize="sm" color="gray.500" fontWeight="medium">There are no remaining available suggestions.</Text>
        </Flex>
    );
}

function AccordionOptions({
    item,
    nexOptionsByVendor,
    invoiceDate,
    invoiceType,
    invoiceVendorName,
    nexLineQuantityBasis = 'qtyRec',
    selectedNexOptionAccordionKey,
    setSelectedNexOptionAccordionKey,
}: {
    item: InvoicePart;
    nexOptionsByVendor: Array<{ vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }>;
    invoiceDate?: string | Date;
    invoiceType?: InvoiceDetailResponse['invoiceType'];
    invoiceVendorName?: string | null;
    nexLineQuantityBasis?: NexLineQuantityBasis;
    selectedNexOptionAccordionKey: string | null;
    setSelectedNexOptionAccordionKey: React.Dispatch<React.SetStateAction<string | null>>;
}) {
    const sessionContext = useContext(SessionContext);
    const showLineStatus = !isInvoicingDemoSession(sessionContext);
    const descriptionColW = showLineStatus ? '19%' : '29%';
    const [sortState, setSortState] = React.useState<SuggestedSortState>(null);
    const [suggestionSearch, setSuggestionSearch] = React.useState('');
    const [showAllCieca, setShowAllCieca] = React.useState(false);
    const toggleSort = (key: SuggestedSortKey) => {
        setSortState((prev) => {
            if (!prev || prev.key !== key) return { key, direction: 'asc' };
            if (prev.direction === 'asc') return { key, direction: 'desc' };
            return null;
        });
    };
    const totalNexOptions = nexOptionsByVendor.reduce((s: number, sec: { lines: { length: number } }) => s + sec.lines.length, 0);
    const qtyColumnLabel = getQtyColumnLabel(invoiceType, nexLineQuantityBasis);
    const qtyColumnTooltip = getQtyColumnTooltip(invoiceType, nexLineQuantityBasis);
    const accordionKeyDefault = '0-0';
    const resolvedAccordionKey = (() => {
        if (selectedNexOptionAccordionKey === NO_VISIBLE_NEX_ACCORDION_KEY) return '';
        const k = selectedNexOptionAccordionKey ?? accordionKeyDefault;
        const [s, l] = k.split('-').map(Number);
        if (nexOptionsByVendor[s]?.lines[l] != null) return k;
        return accordionKeyDefault;
    })();
    React.useLayoutEffect(() => {
        const visibleKeys: string[] = [];
        for (let si = 0; si < nexOptionsByVendor.length; si++) {
            const lines = nexOptionsByVendor[si].lines as InvoicePartWithNex[];
            if (!lines?.length) continue;
            const visible = visibleSuggestedLineIndices(lines, {
                sortState,
                suggestionSearch,
                showAllCieca,
                invoiceType,
                invoicePartNumber: item?.partNumber,
                invoiceVendorName,
                nexLineQuantityBasis,
            });
            for (const li of visible) visibleKeys.push(`${si}-${li}`);
        }
        const next = firstVisibleNexAccordionKey(visibleKeys, selectedNexOptionAccordionKey);
        const current = selectedNexOptionAccordionKey ?? accordionKeyDefault;
        if (next !== current) setSelectedNexOptionAccordionKey(next);
    }, [
        suggestionSearch,
        showAllCieca,
        invoiceType,
        invoiceVendorName,
        item?.partNumber,
        nexOptionsByVendor,
        sortState,
        selectedNexOptionAccordionKey,
        setSelectedNexOptionAccordionKey,
        nexLineQuantityBasis,
    ]);
    if (totalNexOptions === 0) {
        const noRemainingSection = nexOptionsByVendor?.length === 1 && nexOptionsByVendor[0].noRemaining ? nexOptionsByVendor[0] : null;
        return (
            <Flex align="center" justify="center" py={10}>
                <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    {noRemainingSection
                        ? <>No remaining line items from <strong>{noRemainingSection.vendorName}</strong> in the NEX purchase order.</>
                        : 'There are no remaining available suggestions.'}
                </Text>
            </Flex>
        );
    }

    return (
        <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" p={3}>
            <VStack align="stretch" spacing={3}>
                <Box border="1px solid" borderColor="blue.200" borderRadius="md" overflow="hidden" bg="white">
                    <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed' }}>
                        <Thead bg="gray.50">
                            <Tr>
                                <Th py={2} w="11%" color="gray.500" fontSize="xs">PO / Line</Th>
                                <Th py={2} w="11%" color="gray.500" fontSize="xs">Ordered Date</Th>
                                {showLineStatus && (
                                    <Th py={2} w="10%" color="gray.500" fontSize="xs">Line Status</Th>
                                )}
                                <Th py={2} w={descriptionColW} color="gray.500" fontSize="xs">Description</Th>
                                <Th py={2} w="12%" color="gray.500" fontSize="xs">Part Number</Th>
                                <Th py={2} w="6%" textAlign="center" color="gray.500" fontSize="xs" whiteSpace="nowrap">
                                    <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                        <Text
                                            as="span"
                                            display="inline-block"
                                            cursor="help"
                                            fontSize="xs"
                                            textTransform="uppercase"
                                            letterSpacing="0.04em"
                                            whiteSpace="nowrap"
                                        >
                                            {qtyColumnLabel}
                                        </Text>
                                    </Tooltip>
                                </Th>
                                <Th py={2} w="11%" textAlign="right" color="gray.500" fontSize="xs">List Price</Th>
                                <Th py={2} w="11%" textAlign="right" color="gray.500" fontSize="xs">Unit Cost</Th>
                                <Th py={2} w="9%" textAlign="right" color="gray.500" fontSize="xs">Total</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr bg="gray.50">
                                <Td py={2} fontSize="sm" color="gray.500" borderColor="gray.100">—</Td>
                                <Td py={2} fontSize="sm" borderColor="gray.100">{formatDateCell(invoiceDate)}</Td>
                                {showLineStatus && (
                                    <Td py={2} borderColor="gray.100"><LineStatusTag status={undefined} /></Td>
                                )}
                                <Td py={2} fontSize="sm" borderColor="gray.100">{item?.title ?? '—'}</Td>
                                <Td py={2} fontSize="sm" borderColor="gray.100">{item?.partNumber ?? '—'}</Td>
                                <Td py={2} fontSize="sm" textAlign="center" borderColor="gray.100">
                                    <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                        <Text as="span" display="inline-block">{item?.qty ?? '—'}</Text>
                                    </Tooltip>
                                </Td>
                                <Td py={2} fontSize="sm" textAlign="right" borderColor="gray.100">{item?.listPrice != null ? fmtUsd(item.listPrice) : '—'}</Td>
                                <Td py={2} fontSize="sm" textAlign="right" borderColor="gray.100">{item?.unitBuyPrice != null ? fmtUsd(item.unitBuyPrice) : '—'}</Td>
                                <Td py={2} fontSize="sm" textAlign="right" fontWeight="medium" borderColor="gray.100">{item?.totalBuyPrice != null ? fmtUsd(item.totalBuyPrice) : '—'}</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>
                <Text fontSize="sm" color="blue.700">
                    Choose a substitution from the NEX purchase order below (grouped by vendor).
                </Text>
                <Accordion allowMultiple defaultIndex={[0]}>
                    {nexOptionsByVendor.map((section: { vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }, sectionIdx: number) => (
                        <AccordionItem key={sectionIdx} border="1px solid" borderColor="blue.200" borderRadius="md" mb={2} overflow="hidden" _last={{ mb: 0 }}>
                            <AccordionButton bg="gray.50" _expanded={{ bg: 'gray.100' }} py={2} px={3}>
                                <Text fontSize="sm" fontWeight="medium" flex={1} textAlign="left">{section.vendorName}</Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={0} px={0}>
                                {(section.noRemaining || section.lines.length === 0) ? (
                                    <Box py={4} px={3} bg="gray.50">
                                        <Text fontSize="sm" color="gray.600">No remaining line items from <strong>{section.vendorName}</strong> in the NEX purchase order.</Text>
                                    </Box>
                                ) : (
                                    (() => {
                                        const lines = section.lines as InvoicePartWithNex[];
                                        const searchedLineIdxs = searchedSuggestedLineIndices(
                                            lines,
                                            sortState,
                                            suggestionSearch,
                                            nexLineQuantityBasis,
                                        );
                                        const visibleLineIdxs = visibleSuggestedLineIndices(lines, {
                                            sortState,
                                            suggestionSearch,
                                            showAllCieca,
                                            invoiceType,
                                            invoicePartNumber: item?.partNumber,
                                            invoiceVendorName,
                                            nexLineQuantityBasis,
                                        });
                                        const hiddenCiecaCount = searchedLineIdxs.length - visibleLineIdxs.length;
                                        const ciecaFilterLabel = defaultNexSuggestionFilterLabel(invoiceType);
                                        const suggestToolbar = (
                                            <Box
                                                position="sticky"
                                                top={0}
                                                zIndex={2}
                                                w="100%"
                                                bg="white"
                                                borderBottomWidth="1px"
                                                borderBottomColor="gray.100"
                                                px={3}
                                                py={2}
                                            >
                                                <Flex
                                                    direction={{ base: 'column', lg: 'row' }}
                                                    align={{ base: 'stretch', lg: 'center' }}
                                                    gap={3}
                                                    justify="space-between"
                                                    w="100%"
                                                >
                                                    <Text fontSize="sm" color="gray.700" flex="1" minW={0}>
                                                        These are the remaining line items from <strong>{section.vendorName}</strong> in the NEX response (already-matched lines are excluded).
                                                        {ciecaFilterLabel && hiddenCiecaCount > 0 && !showAllCieca ? (
                                                            <> {ciecaFilterLabel}. </>
                                                        ) : null}
                                                    </Text>
                                                    <Flex align="center" gap={2} flexShrink={0}>
                                                        {hiddenCiecaCount > 0 || showAllCieca ? (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="blue"
                                                                onClick={() => setShowAllCieca((v) => !v)}
                                                            >
                                                                {showAllCieca ? "Show fewer" : `Show more (${hiddenCiecaCount})`}
                                                            </Button>
                                                        ) : null}
                                                    <InputGroup
                                                        size="sm"
                                                        flexShrink={0}
                                                        flex={{ base: 'none', lg: '0 0 25%' }}
                                                        w="25%"
                                                        maxW="25%"
                                                        alignSelf={{ base: 'flex-end', lg: 'stretch' }}
                                                    >
                                                        <InputLeftElement pointerEvents="none" h="32px">
                                                            <SearchIcon color="gray.400" boxSize={3.5} />
                                                        </InputLeftElement>
                                                        <Input
                                                            w="100%"
                                                            minW={0}
                                                            pl={9}
                                                            placeholder="Search by description, part #, PO, status, amounts…"
                                                            value={suggestionSearch}
                                                            onChange={(e) => setSuggestionSearch(e.target.value)}
                                                            bg="white"
                                                            borderColor="blue.200"
                                                        />
                                                    </InputGroup>
                                                    </Flex>
                                                </Flex>
                                            </Box>
                                        );
                                        if (visibleLineIdxs.length === 0) {
                                            return (
                                                <Box overflowY="auto" bg="white">
                                                    {suggestToolbar}
                                                    <Box py={6} px={3}>
                                                        <Text fontSize="sm" color="gray.500" textAlign="center">
                                                            {searchedLineIdxs.length === 0
                                                                ? `No lines match this search for ${section.vendorName}.`
                                                                : "No matching sublet/OEM lines. Use Show more to see every remaining NEX line."}
                                                        </Text>
                                                    </Box>
                                                </Box>
                                            );
                                        }
                                        return (
                                    <Box overflowY="auto" bg="white">
                                        {suggestToolbar}
                                    <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed' }}>
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th py={2} w="11%">{renderSortableHeader('PO / Line', 'poLine', sortState, toggleSort)}</Th>
                                                <Th py={2} w="11%">{renderSortableHeader('Ordered Date', 'orderedDate', sortState, toggleSort)}</Th>
                                                {showLineStatus && (
                                                    <Th py={2} w="10%">{renderSortableHeader('Line Status', 'lineStatus', sortState, toggleSort)}</Th>
                                                )}
                                                <Th py={2} w={descriptionColW}>{renderSortableHeader('Description', 'description', sortState, toggleSort)}</Th>
                                                <Th py={2} w="12%">{renderSortableHeader('Part Number', 'partNumber', sortState, toggleSort)}</Th>
                                                <QtyColumnHeader sortState={sortState} toggleSort={toggleSort} qtyColumnLabel={qtyColumnLabel} qtyColumnTooltip={qtyColumnTooltip} compact />
                                                <Th py={2} w="11%">{renderSortableHeader('List Price', 'listPrice', sortState, toggleSort, true)}</Th>
                                                <Th py={2} w="11%">{renderSortableHeader('Unit Cost', 'unitCost', sortState, toggleSort, true)}</Th>
                                                <Th py={2} w="9%">{renderSortableHeader('Total', 'total', sortState, toggleSort, true)}</Th>
                                            </Tr>
                                        </Thead>
                                        {visibleLineIdxs.map((lineIdx: number) => {
                                            const opt = lines[lineIdx];
                                            const rowKey = `${sectionIdx}-${lineIdx}`;
                                            const isSelected = rowKey === resolvedAccordionKey;
                                            return (
                                                <Tbody
                                                    key={`${section.vendorName}-${lineIdx}-${opt.partNumber}`}
                                                    bg={isSelected ? 'blue.50' : 'white'}
                                                    borderLeft={isSelected ? '4px solid' : 'none'}
                                                    borderLeftColor={isSelected ? 'blue.500' : 'transparent'}
                                                    _hover={{ bg: isSelected ? 'blue.100' : 'gray.100', cursor: 'pointer' }}
                                                    onClick={() => setSelectedNexOptionAccordionKey(rowKey)}
                                                    borderBottom="1px solid #E2E8F0"
                                                >
                                                    <Tr>
                                                        <Td py={2} w="11%" fontSize="xs" fontFamily="mono" borderBottom="none" whiteSpace="nowrap">
                                                            {(() => {
                                                                const ext2 = opt as unknown as Record<string, unknown>;
                                                                const poID = ext2.poHeaderID ?? ext2.po_header_id;
                                                                const ln = ext2.lineNumber ?? ext2.line_number;
                                                                return poID != null && poID !== ''
                                                                    ? `${String(poID)} / L${ln != null && ln !== '' ? String(ln) : '—'}`
                                                                    : '—';
                                                            })()}
                                                        </Td>
                                                        <Td py={2} w="11%" fontSize="sm" borderBottom="none">{formatDateCell((opt as InvoicePartWithNex)._poOrderedDate)}</Td>
                                                        {showLineStatus && (
                                                            <Td py={2} w="10%" borderBottom="none"><LineStatusTag status={(opt as InvoicePartWithNex).lineStatus} /></Td>
                                                        )}
                                                        <Td py={2} w={descriptionColW} fontSize="sm" borderBottom="none">{opt.title ?? '—'}</Td>
                                                        <Td py={2} w="12%" fontSize="sm" borderBottom="none">{opt.partNumber ?? '—'}</Td>
                                                        <Td py={2} w="6%" fontSize="sm" textAlign="center" borderBottom="none">
                                                            <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                                                <Text as="span" display="inline-block">
                                                                    {nexQtyReceivedForDisplay(
                                                                        opt as InvoicePartWithNex,
                                                                        nexLineQuantityBasis,
                                                                    )}
                                                                </Text>
                                                            </Tooltip>
                                                        </Td>
                                                        <Td py={2} w="11%" fontSize="sm" textAlign="right" borderBottom="none">{opt.listPrice != null ? fmtUsd(opt.listPrice) : '—'}</Td>
                                                        <Td py={2} w="11%" fontSize="sm" textAlign="right" borderBottom="none">{opt.unitBuyPrice != null ? fmtUsd(opt.unitBuyPrice) : '—'}</Td>
                                                        <Td py={2} w="9%" fontSize="sm" textAlign="right" fontWeight="medium" borderBottom="none">{opt.totalBuyPrice != null ? fmtUsd(opt.totalBuyPrice) : '—'}</Td>
                                                    </Tr>
                                                </Tbody>
                                            );
                                        })}
                                    </Table>
                                    </Box>
                                        );
                                    })()
                                )}
                            </AccordionPanel>
                        </AccordionItem>
                    ))}
                </Accordion>
            </VStack>
        </Box>
    );
}
